import { Button, Card, Icon, Menu, Spinner, Tag, Text } from "@ttc3k/trekker";
import {
  UserAccessOperators,
  OperatorStaffQuery,
  EnumEntityReferenceType,
} from "gql/generated";
import { Flex, styled } from "styled-system/jsx";
import { MoreHorizCircle } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { CopyToClipboard } from "shared/components";
import { TagAction, TagStatus, useStaffCard } from "./useStaffCard";

type StaffCardProps = {
  _entityId: string;
  type: EnumEntityReferenceType;
  staffMember: OperatorStaffQuery["operatorStaff"][0];
  accessTo: UserAccessOperators[];
  viewOnly?: boolean;
  onAcceptClick: () => void;
  onRemoveClick: (action: TagAction) => void;
  onResendInviteClick: () => void;
  onSendInviteClick: () => void;
  updatingId: string | undefined;
};

export const StaffCard = ({
  _entityId,
  type,
  staffMember,
  viewOnly = false,
  onAcceptClick,
  onRemoveClick,
  onResendInviteClick,
  onSendInviteClick,
  updatingId,
}: StaffCardProps) => {
  const { t } = useTranslation();
  const { classes, fullName, tagStatus, handleStaffAction } = useStaffCard({
    _entityId,
    type,
    staffMember,
    onAcceptClick,
    onRemoveClick,
    onResendInviteClick,
    onSendInviteClick,
  });

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  return (
    <Card.Surface
      className={classes.root}
      minH="140px"
      flex="1 0 0"
      minW="250px"
    >
      <Card.Body>
        <Card.BodyContent
          flexDir="column"
          justifyContent="space-between"
          flex="1 0 0"
        >
          <Flex
            flexDir="row"
            justifyContent="space-between"
            alignItems="flex-start"
            gap="400"
          >
            <Flex flexDir="row" alignItems="center" gap="150">
              {staffMember.picture && (
                <>
                  {!isImageLoaded && (
                    <Spinner color="text.mid" width="32px" height="32px" />
                  )}
                  <styled.img
                    src={staffMember.picture}
                    width="32px"
                    height="32px"
                    alt={fullName}
                    aria-hidden
                    display={isImageLoaded ? "block" : "none"}
                    onLoad={() => setIsImageLoaded(true)}
                  />
                </>
              )}
              <Text visual="bodySemiBold">{fullName}</Text>
            </Flex>

            {/*
              We are ONLY showing the tag for invites now (Owner and Staff are hidden)
              Previous tag code was
              <Tag
                visual={
                  tagStatus === TagStatus.Invited
                    ? "accent.purple"
                    : "accent.blue"
                }
                mode={tagStatus === TagStatus.Owner ? "dark" : "light"}
              >
                {t(`core:STAFF.${tagStatus}`)}
              </Tag>
            */}
            {[
              TagStatus.Invited,
              TagStatus.NotInvited,
              TagStatus.Requested,
            ].includes(tagStatus) && (
              <Tag
                visual={
                  tagStatus === TagStatus.NotInvited
                    ? "warning"
                    : tagStatus === TagStatus.Invited
                      ? "accent.purple"
                      : "accent.magenta"
                }
                mode={"light"}
                minW={"58px"}
              >
                {t(`core:STAFF.${tagStatus}`)}
              </Tag>
            )}
          </Flex>
          <Flex
            flexDir="row"
            justifyContent="space-between"
            alignItems="center"
            gap="200"
          >
            <CopyToClipboard text={staffMember.email} />

            {updatingId === staffMember._id && (
              <Spinner color="text.mid" width="24px" height="24px" />
            )}
            {!viewOnly &&
              updatingId !== staffMember._id &&
              tagStatus !== TagStatus.Owner && (
                <Menu.Root
                  trigger={
                    <Button visual="unstyled" borderRadius="full">
                      <Icon
                        Element={MoreHorizCircle}
                        color="icon.light"
                        size="lg"
                      />
                    </Button>
                  }
                  positioning={{
                    placement: "bottom",
                    offset: {
                      crossAxis: -70,
                    },
                  }}
                  onSelect={(item: { value: string }) =>
                    handleStaffAction(item)
                  }
                >
                  {tagStatus === TagStatus.Requested && (
                    <>
                      <Menu.Item
                        value={TagAction.Accept}
                        justifyContent={"flex-start"}
                        color="text.mid"
                      >
                        {t("core:STAFF.CTA_BUTTON.ACCEPT")}
                      </Menu.Item>
                      <Menu.Item
                        value={TagAction.Delete}
                        justifyContent={"flex-start"}
                        color="text.error.mid"
                      >
                        {t("core:STAFF.CTA_BUTTON.DELETE")}...
                      </Menu.Item>
                    </>
                  )}
                  {tagStatus === TagStatus.NotInvited && (
                    <Menu.Item
                      value={TagAction.Invite}
                      justifyContent={"flex-start"}
                      color="text.mid"
                    >
                      {t("core:STAFF.CTA_BUTTON.SEND_INVITE")}
                    </Menu.Item>
                  )}
                  {tagStatus === TagStatus.Invited && (
                    <Menu.Item
                      value={TagAction.Resend}
                      justifyContent={"flex-start"}
                      color="text.mid"
                    >
                      {t("core:STAFF.CTA_BUTTON.REINVITE")}
                    </Menu.Item>
                  )}
                  {tagStatus !== TagStatus.Requested && (
                    <Menu.Item
                      value={TagAction.Remove}
                      justifyContent={"flex-start"}
                      color="text.error.mid"
                    >
                      {t("core:STAFF.CTA_BUTTON.REMOVE")}...
                    </Menu.Item>
                  )}
                </Menu.Root>
              )}
          </Flex>
        </Card.BodyContent>
      </Card.Body>
    </Card.Surface>
  );
};
